<script setup>
import { computed, nextTick, ref, useId } from 'vue';

const props = defineProps({
  modelValue: { type: String, default: '' },
  label: { type: String, default: '' },
  placeholder: { type: String, default: '' },
  options: { type: Array, default: () => [] },
});

const emit = defineEmits(['update:modelValue']);

defineOptions({ inheritAttrs: false });

const inputRef = ref(null);
const optionRefs = ref([]);
const isOpen = ref(false);
const query = ref('');
const triggerStart = ref(-1);
const activeIndex = ref(0);
const componentId = useId();
const inputId = computed(() => `liquid-variable-input-${componentId}`);
const listboxId = computed(() => `liquid-variable-listbox-${componentId}`);
const optionId = index => `liquid-variable-option-${componentId}-${index}`;

const filteredOptions = computed(() => {
  const normalizedQuery = query.value.trim().toLowerCase();
  if (!normalizedQuery) return props.options;

  return props.options.filter(option =>
    `${option.label} ${option.token}`.toLowerCase().includes(normalizedQuery)
  );
});

const activeOptionId = computed(() =>
  isOpen.value && filteredOptions.value.length
    ? optionId(activeIndex.value)
    : undefined
);

const updateSuggestions = event => {
  const input = event.target;
  const value = input.value;
  const cursor = input.selectionStart ?? value.length;
  const beforeCursor = value.slice(0, cursor);
  const doubleBrace = beforeCursor.match(/{{\s*([\w.[\]"'-]*)$/);
  const singleBrace = doubleBrace ? null : beforeCursor.match(/{$/);
  const match = doubleBrace || singleBrace;

  emit('update:modelValue', value);

  if (!match) {
    isOpen.value = false;
    triggerStart.value = -1;
    return;
  }

  triggerStart.value = cursor - match[0].length;
  query.value = doubleBrace?.[1] || '';
  activeIndex.value = 0;
  isOpen.value = true;
};

const insertOption = async option => {
  const input = inputRef.value;
  const value = props.modelValue || '';
  const cursor = input?.selectionStart ?? value.length;
  const start = triggerStart.value >= 0 ? triggerStart.value : cursor;
  const nextValue = `${value.slice(0, start)}${option.token}${value.slice(cursor)}`;
  const nextCursor = start + option.token.length;

  emit('update:modelValue', nextValue);
  isOpen.value = false;
  triggerStart.value = -1;

  await nextTick();
  inputRef.value?.focus();
  inputRef.value?.setSelectionRange(nextCursor, nextCursor);
};

const scrollActiveOptionIntoView = async () => {
  await nextTick();
  optionRefs.value[activeIndex.value]?.scrollIntoView?.({ block: 'nearest' });
};

const handleKeydown = event => {
  if (!isOpen.value || !filteredOptions.value.length) return;

  if (event.key === 'ArrowDown') {
    event.preventDefault();
    activeIndex.value = (activeIndex.value + 1) % filteredOptions.value.length;
    scrollActiveOptionIntoView();
  } else if (event.key === 'ArrowUp') {
    event.preventDefault();
    activeIndex.value =
      (activeIndex.value - 1 + filteredOptions.value.length) %
      filteredOptions.value.length;
    scrollActiveOptionIntoView();
  } else if (event.key === 'Enter') {
    event.preventDefault();
    insertOption(filteredOptions.value[activeIndex.value]);
  } else if (event.key === 'Tab') {
    isOpen.value = false;
  } else if (event.key === 'Escape') {
    event.preventDefault();
    isOpen.value = false;
  }
};

const closeSuggestions = () => {
  window.setTimeout(() => {
    isOpen.value = false;
  }, 100);
};
</script>

<template>
  <div class="relative flex min-w-0 flex-col gap-1">
    <label
      v-if="label"
      :for="inputId"
      class="mb-0.5 text-sm font-medium text-n-slate-12"
    >
      {{ label }}
    </label>
    <input
      v-bind="$attrs"
      :id="inputId"
      ref="inputRef"
      :value="modelValue"
      :placeholder="placeholder"
      type="text"
      role="combobox"
      autocomplete="off"
      :aria-expanded="isOpen"
      :aria-controls="listboxId"
      :aria-activedescendant="activeOptionId"
      aria-autocomplete="list"
      class="block h-10 w-full rounded-lg border-0 bg-n-alpha-black2 px-3 py-2.5 text-sm text-n-slate-12 outline outline-1 outline-offset-[-1px] outline-n-weak transition-all placeholder:text-n-slate-10 hover:outline-n-slate-6 focus:outline-n-brand"
      @input="updateSuggestions"
      @keydown="handleKeydown"
      @blur="closeSuggestions"
    />
    <div
      v-if="isOpen"
      :id="listboxId"
      role="listbox"
      data-testid="liquid-variable-suggestions"
      class="absolute left-0 right-0 top-full z-30 mt-1 max-h-64 overflow-y-auto rounded-lg border border-n-weak bg-n-solid-2 p-1 shadow-xl"
    >
      <button
        v-for="(option, index) in filteredOptions"
        :id="optionId(index)"
        ref="optionRefs"
        :key="option.token"
        type="button"
        role="option"
        :aria-selected="index === activeIndex"
        class="flex w-full items-center justify-between gap-3 rounded-md px-3 py-2 text-left text-sm text-n-slate-12 hover:bg-n-alpha-2"
        :class="{ 'bg-n-alpha-2': index === activeIndex }"
        @pointerdown.prevent="insertOption(option)"
      >
        <span class="truncate">{{ option.label }}</span>
        <code class="shrink-0 text-xs text-n-blue-11">{{ option.token }}</code>
      </button>
      <p
        v-if="!filteredOptions.length"
        class="px-3 py-2 text-sm text-n-slate-10"
      >
        {{ placeholder }}
      </p>
    </div>
  </div>
</template>
